// src/services/agentClient.js
//
// Cliente de streaming SSE para el agente NutriAgent.
//
// El endpoint es POST {VITE_AGENT_URL}/agent/chat con cabecera
// Authorization: Bearer <token> y cuerpo JSON. Como necesitamos POST +
// cabeceras personalizadas no podemos usar EventSource (solo soporta GET sin
// cabeceras), así que hacemos fetch() y leemos el cuerpo de la respuesta con
// response.body.getReader(), parseando el protocolo SSE manualmente.
//
// Contrato de eventos SSE del backend (cada evento es un bloque separado por
// una línea en blanco "\n\n", con líneas "event:" y "data:"):
//   token           { text }                    -> fragmento de texto del asistente
//   tool_start      { tool }                    -> el agente empieza a usar una tool
//   tool_end        { tool }                    -> el agente termina de usar una tool
//   action_required { conversationId, actions } -> el agente pausa esperando que el
//                                                  usuario confirme una o varias
//                                                  acciones que MODIFICAN datos.
//                                                  Tras este evento llega un "done"
//                                                  que cierra el stream con el turno
//                                                  PAUSADO. El usuario decide y se
//                                                  reanuda el hilo (ver `resume`).
//   done            { conversationId }           -> fin del turno (id para continuar hilo)
//   error           { message, code }            -> error; code 401 => sesión caducada

// URL base del agente. Variable propia para no mezclarla con la API principal.
const AGENT_URL =
  import.meta.env.VITE_AGENT_URL || 'https://agent.nutrizia.app';

/**
 * Parsea un bloque SSE crudo (texto entre dos "\n\n") en { event, data }.
 * - "event:" indica el tipo de evento (por defecto "message").
 * - Las líneas "data:" se concatenan (puede haber varias por bloque).
 * Devuelve null si el bloque no contiene datos útiles (p.ej. comentarios ":").
 *
 * @param {string} rawBlock
 * @returns {{ event: string, data: any } | null}
 */
const parseSSEBlock = (rawBlock) => {
  const lines = rawBlock.split('\n');
  let eventName = 'message';
  const dataLines = [];

  for (const line of lines) {
    // Comentarios SSE (heartbeats) empiezan por ":" -> se ignoran.
    if (!line || line.startsWith(':')) continue;

    if (line.startsWith('event:')) {
      eventName = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      // slice(5) en vez de replace para conservar espacios internos del payload.
      dataLines.push(line.slice('data:'.length).replace(/^ /, ''));
    }
  }

  if (dataLines.length === 0) return null;

  const rawData = dataLines.join('\n');
  let data = rawData;
  try {
    data = JSON.parse(rawData);
  } catch {
    // Si no es JSON válido lo dejamos como string crudo.
    data = rawData;
  }

  return { event: eventName, data };
};

/**
 * Despacha un evento SSE ya parseado al handler correspondiente.
 *
 * @param {{ event: string, data: any }} parsed
 * @param {object} handlers
 * @returns {boolean} true si el stream debe darse por terminado (done/error).
 */
const dispatchEvent = (parsed, handlers) => {
  const { event, data } = parsed;
  const {
    onToken,
    onToolStart,
    onToolEnd,
    onActionRequired,
    onDone,
    onError,
  } = handlers;

  switch (event) {
    case 'token':
      if (onToken) onToken(data?.text ?? '');
      return false;

    case 'tool_start':
      if (onToolStart) onToolStart(data?.tool ?? '');
      return false;

    case 'tool_end':
      if (onToolEnd) onToolEnd(data?.tool ?? '');
      return false;

    case 'action_required':
      // El agente pausa esperando confirmación del usuario. NO terminamos el
      // stream aquí: el "done" que llega justo después es quien lo cierra.
      if (onActionRequired) {
        onActionRequired({
          conversationId: data?.conversationId ?? null,
          actions: Array.isArray(data?.actions) ? data.actions : [],
        });
      }
      return false;

    case 'done':
      if (onDone) onDone(data?.conversationId ?? null);
      return true;

    case 'error':
      if (onError) {
        onError({
          message: data?.message || 'Error desconocido del agente',
          code: data?.code,
        });
      }
      return true;

    default:
      // Eventos desconocidos: los ignoramos silenciosamente.
      return false;
  }
};

/**
 * Abre un stream de chat con el agente y despacha los eventos a los handlers.
 *
 * Hay dos modos de uso:
 *  - Mensaje normal: se envía `message` (+ conversationId/screenContext).
 *  - Reanudación (human-in-the-loop): se envía `resume` (p.ej. { confirm: true })
 *    junto con `conversationId`, y SIN `message`. Sirve para que el usuario
 *    confirme o cancele las acciones de un evento `action_required` previo.
 *
 * @param {object} params
 * @param {string} params.authToken        - JWT del usuario (Bearer).
 * @param {string} [params.message]        - Mensaje del usuario (modo normal).
 * @param {string|null} [params.conversationId] - Id del hilo para continuidad.
 * @param {object} [params.screenContext]  - Contexto de pantalla, p.ej. { route }.
 * @param {object} [params.resume]         - Decisión de reanudación, p.ej. { confirm: boolean }.
 * @param {AbortSignal} [params.signal]    - Señal para cancelar el stream.
 * @param {object} handlers
 * @param {(text: string) => void} [handlers.onToken]
 * @param {(tool: string) => void} [handlers.onToolStart]
 * @param {(tool: string) => void} [handlers.onToolEnd]
 * @param {(info: { conversationId: string|null, actions: Array }) => void} [handlers.onActionRequired]
 * @param {(conversationId: string|null) => void} [handlers.onDone]
 * @param {(err: { message: string, code?: number }) => void} [handlers.onError]
 * @returns {Promise<void>}
 */
export const streamAgentChat = async (
  { authToken, message, conversationId, screenContext, resume, signal },
  handlers = {}
) => {
  const { onError, onDone } = handlers;

  let response;
  try {
    response = await fetch(`${AGENT_URL}/agent/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        Authorization: `Bearer ${authToken}`,
      },
      // En reanudación enviamos { conversationId, resume } SIN "message".
      // En un turno normal, el cuerpo de siempre.
      body: JSON.stringify(
        resume
          ? {
              conversationId: conversationId || null,
              resume,
            }
          : {
              message,
              conversationId: conversationId || null,
              screenContext: screenContext || {},
            }
      ),
      signal,
    });
  } catch (err) {
    // Si fue una cancelación deliberada (AbortController) no es un error real.
    if (err?.name === 'AbortError') return;
    if (onError) {
      onError({ message: 'No se pudo conectar con el asistente.' });
    }
    return;
  }

  // Errores HTTP antes de empezar a streamear (p.ej. 401 sin cuerpo SSE).
  if (!response.ok) {
    if (response.status === 401) {
      if (onError) {
        onError({ message: 'Tu sesión ha caducado.', code: 401 });
      }
      return;
    }
    // Intentamos extraer un mensaje del cuerpo si lo hay.
    let message401;
    try {
      const body = await response.json();
      message401 = body?.message;
    } catch {
      // ignorar
    }
    if (onError) {
      onError({
        message: message401 || `Error del servidor (${response.status}).`,
        code: response.status,
      });
    }
    return;
  }

  if (!response.body) {
    if (onError) {
      onError({ message: 'El asistente no devolvió ningún contenido.' });
    }
    return;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let finished = false;

  try {
    // Bucle de lectura del stream.
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Los eventos SSE se separan por una línea en blanco ("\n\n").
      // Normalizamos posibles "\r\n" antes de partir.
      let separatorIndex;
      buffer = buffer.replace(/\r\n/g, '\n');
      while ((separatorIndex = buffer.indexOf('\n\n')) !== -1) {
        const rawBlock = buffer.slice(0, separatorIndex);
        buffer = buffer.slice(separatorIndex + 2);

        const parsed = parseSSEBlock(rawBlock);
        if (!parsed) continue;

        const shouldStop = dispatchEvent(parsed, handlers);
        if (shouldStop) {
          finished = true;
          break;
        }
      }

      if (finished) break;
    }

    // Procesa cualquier bloque residual que quede en el buffer al cerrar.
    if (!finished && buffer.trim()) {
      const parsed = parseSSEBlock(buffer);
      if (parsed) {
        dispatchEvent(parsed, handlers);
        finished = true;
      }
    }

    // Si el stream terminó sin un evento "done" explícito, cerramos el turno
    // igualmente para que la UI no quede en estado de carga.
    if (!finished && onDone) {
      onDone(conversationId || null);
    }
  } catch (err) {
    if (err?.name === 'AbortError') {
      // Cancelación del usuario: silenciosa.
      return;
    }
    if (onError) {
      onError({ message: 'Se interrumpió la conexión con el asistente.' });
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {
      // ignorar
    }
  }
};

export default streamAgentChat;
